'use client';

import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShieldAlert, DollarSign, Activity, Bitcoin, FileText, Handshake, KeyRound } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ADMIN_ROUTES } from '@/config/router';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { canSeeIamDirectory } from './iam/access';

interface AdminLayoutProps {
  children: React.ReactNode;
}

/**
 * Admin section shell.
 *
 * The IAM tab is the only admin surface open beyond superadmins: to
 * the roles the server lets read the directory (`canSeeIamDirectory`),
 * and per-row mutations are gated by the IAM policy layer
 * (`canAssignTgdRole`, `canManagePartnerStaff`). The other tabs
 * (financial reporting, partner registry) stay superadmin-only
 * because they expose platform-wide privileged data.
 *
 * The legacy "Staff" tab (internal-only role grid) was folded into
 * IAM: filter by Scope = "Internal Tagada" to get the same view,
 * with the bonus of inline edit, search, and a unified directory.
 *
 * Non-superadmins see only the IAM tab — the other tabs are hidden so
 * the chrome itself broadcasts "this is the slice of admin you can
 * use", instead of showing tabs that bounce them to an Access Denied
 * screen.
 *
 * The TagadaRx desk is not here: it is a support surface, not an admin
 * one, and lives under `/rx` (see `RX_ROUTES`).
 */
export default function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const claims = useSessionClaims();
  const { isSuperAdmin, isLoaded, isSignedIn } = claims;
  const canSeeIam = canSeeIamDirectory(claims);

  const isOnIam = location.pathname.includes('/admin/iam');

  const getCurrentTab = () => {
    if (location.pathname.includes('/admin/partners')) return 'partners';
    if (location.pathname.includes('/admin/iam')) return 'iam';
    if (location.pathname.endsWith('/processing')) return 'processing';
    if (location.pathname.endsWith('/crypto')) return 'crypto';
    if (location.pathname.endsWith('/invoiced')) return 'invoiced';
    return 'revenue';
  };

  const handleTabChange = (value: string) => {
    const search = location.search;
    switch (value) {
      case 'revenue':
        navigate(ADMIN_ROUTES.REVENUE + search);
        break;
      case 'processing':
        navigate(ADMIN_ROUTES.PROCESSING + search);
        break;
      case 'crypto':
        navigate(ADMIN_ROUTES.CRYPTO + search);
        break;
      case 'invoiced':
        navigate(ADMIN_ROUTES.INVOICED + search);
        break;
      case 'partners':
        navigate(ADMIN_ROUTES.PARTNERS);
        break;
      case 'iam':
        navigate(ADMIN_ROUTES.IAM);
        break;
    }
  };

  if (!isLoaded) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    );
  }

  // Signed-out: hard block. The CRM isn't a public surface.
  if (!isSignedIn) {
    return (
      <div className="bg-background flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="text-muted-foreground mx-auto h-12 w-12" />
          <h2 className="mt-4 text-xl font-semibold">Sign in required</h2>
          <p className="text-muted-foreground mt-2 max-w-md">
            Sign in to access the admin section.
          </p>
        </div>
      </div>
    );
  }

  // Non-superadmins land here only for /admin/iam, and only with a
  // directory role. Anything else gets the Access Denied screen, with a
  // bounce to the IAM tab for those who may read the directory.
  if (!isSuperAdmin && (!isOnIam || !canSeeIam)) {
    return (
      <div className="bg-background flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="text-muted-foreground mx-auto h-12 w-12" />
          <h2 className="mt-4 text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground mt-2 max-w-md">
            This section is restricted to superadmin users.
          </p>
          {canSeeIam ? (
            <button
              type="button"
              onClick={() => navigate(ADMIN_ROUTES.IAM)}
              className="mt-4 text-sm font-medium underline underline-offset-2"
            >
              Open IAM directory instead
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Admin Backoffice</h1>
              {isSuperAdmin ? (
                <Badge variant="secondary" className="text-xs">superadmin</Badge>
              ) : null}
            </div>
            <p className="text-muted-foreground text-sm sm:text-base">
              {isSuperAdmin ? 'Platform overview and revenue analytics' : 'Identity & access management'}
            </p>
          </div>
        </div>

        <Tabs value={getCurrentTab()} onValueChange={handleTabChange} className="w-full">
          <TabsList className="mb-6 w-full sm:w-auto">
            {isSuperAdmin ? (
              <>
                <TabsTrigger value="revenue" className="gap-2">
                  <DollarSign className="h-4 w-4" />
                  Expected Revenue
                </TabsTrigger>
                <TabsTrigger value="invoiced" className="gap-2">
                  <FileText className="h-4 w-4" />
                  CRM Invoiced
                </TabsTrigger>
                <TabsTrigger value="processing" className="gap-2">
                  <Activity className="h-4 w-4" />
                  Processing
                </TabsTrigger>
                <TabsTrigger value="crypto" className="gap-2">
                  <Bitcoin className="h-4 w-4" />
                  Crypto
                </TabsTrigger>
                <TabsTrigger value="partners" className="gap-2">
                  <Handshake className="h-4 w-4" />
                  Partners
                </TabsTrigger>
              </>
            ) : null}
            <TabsTrigger value="iam" className="gap-2">
              <KeyRound className="h-4 w-4" />
              IAM
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div>{children}</div>
      </div>
    </div>
  );
}
